import { useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import AuthContext from "src/app/contexts/auth-context";
import { useGetPaymentLocationScopesQuery } from "src/generated/graphql";
import {
  defaultLocationScope,
  LocationScope,
  LocationScopeGroup,
} from "./LocationScopePicker";

const STORAGE_KEY = "aemma.location-scope";

/** A scope chosen in the header; only valid for the user and working location it was chosen with. */
export type StoredLocationScope = LocationScope & {
  userId: number;
  sessionLocationId: number | null;
};

const listeners = new Set<() => void>();
let current: StoredLocationScope | null | undefined;

const load = (): StoredLocationScope | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredLocationScope) : null;
  } catch {
    return null;
  }
};

const notify = () => listeners.forEach((listener) => listener());

const onStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_KEY) return;
  current = load();
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
};

const getSnapshot = () => {
  if (current === undefined) current = load();
  return current;
};

export const writeStoredLocationScope = (scope: StoredLocationScope | null) => {
  current = scope;
  try {
    if (scope) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the scope still applies until the page reloads.
  }
  notify();
};

const isValidStoredScope = (
  stored: StoredLocationScope | null,
  userId: number,
  sessionLocationId: number | null,
  group: LocationScopeGroup | undefined
): stored is StoredLocationScope =>
  !!stored &&
  !!group &&
  stored.userId === userId &&
  stored.sessionLocationId === sessionLocationId &&
  stored.companyId === group.companyId &&
  Array.isArray(stored.locationIds) &&
  stored.locationIds.every((id) => group.locations.some((l) => l.id === id));

/**
 * The locations whose payments and sales vouchers the user is viewing. Defaults to all locations for finance
 * users and to the working (session) location for others; the header picker can change it.
 */
export const useLocationScope = () => {
  const currentUser = useContext(AuthContext)?.authContextData?.currentUser;
  const userId = Number(currentUser?.id);
  const sessionLocationId = currentUser?.locationId ?? null;
  const sessionCompanyId = Number(
    currentUser?.location?.companyId ?? currentUser?.companyId
  );

  const { data, error } = useGetPaymentLocationScopesQuery({
    skip: !currentUser,
  });
  const stored = useSyncExternalStore(subscribe, getSnapshot);

  const groups = useMemo<LocationScopeGroup[]>(
    () => data?.paymentLocationScopes ?? [],
    [data]
  );
  const defaultScope = useMemo(
    () => defaultLocationScope(groups, sessionCompanyId),
    [groups, sessionCompanyId]
  );
  const scope = useMemo(() => {
    const group = groups.find((g) => g.companyId === defaultScope?.companyId);
    if (isValidStoredScope(stored, userId, sessionLocationId, group))
      return { companyId: stored.companyId, locationIds: stored.locationIds };
    if (defaultScope || !error || !sessionCompanyId) return defaultScope;
    // Without the accessible locations the lists still load for the working location.
    return {
      companyId: sessionCompanyId,
      locationIds: sessionLocationId ? [Number(sessionLocationId)] : [],
    };
  }, [
    groups,
    defaultScope,
    stored,
    userId,
    sessionLocationId,
    error,
    sessionCompanyId,
  ]);

  const setScope = useCallback(
    (next: LocationScope, nextSessionLocationId: number | null = sessionLocationId) =>
      writeStoredLocationScope({
        ...next,
        userId,
        sessionLocationId: nextSessionLocationId,
      }),
    [userId, sessionLocationId]
  );

  return {
    groups,
    scope,
    defaultScope,
    setScope,
    sessionCompanyId,
    sessionLocationId,
    loaded: !!data,
    error,
  };
};
